import type {
  AdminApiAiUsage,
  AdminApiAiUsageByFeature,
} from "@repo/app-types/entities/creditUsage";
import { resolvePeriodCreditCapacity } from "@repo/app-types/entities/metronome";
import {
  getAiFeatureDefinition,
  USAGE_BASED_FEATURE_UNIT_NOUNS,
} from "@repo/app-types/helpers/aiFeatureCatalog";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { Fragment, type FC } from "react";
import { useAiUsageQuery } from "../../../api/entities/company.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import {
  CreditUsageCardSkeleton,
  CreditUsageThresholdBanner,
  projectPeriodTotal,
  UsageBar,
  useCreditUsageSection,
} from "./creditUsageSection.js";
import {
  formatPeriodLabel,
  formatPeriodResetDate,
  formatUsageAgainstIncluded,
  formatWholeNumber,
} from "./usageFormatting.js";

interface AiCreditsSectionProps {
  periodOffset: number;
}

/**
 * The AI usage section of the billing page: usage bar against Metronome's
 * current-period credit budget and the per-feature breakdown, for the period
 * the page-level header selects.
 * Usage-only preview — nothing here bills, alerts, or pauses features.
 */
export const AiCreditsSection: FC<AiCreditsSectionProps> = (props) => {
  const { periodOffset } = props;
  const { selectedUsage, isUsageLoading } = useCreditUsageSection(
    useAiUsageQuery,
    periodOffset,
  );

  const usageBar = selectedUsage ? resolveUsageBar(selectedUsage) : null;

  return (
    <section className="space-y-6">
      <div className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">AI credits</h2>
        {selectedUsage &&
        usageBar &&
        selectedUsage.selected_period.offset === 0 ? (
          <CreditUsageThresholdBanner
            percentage={(usageBar.consumed / usageBar.capacity) * 100}
            isUncovered={selectedUsage.credits_overage > 0}
          />
        ) : null}
        {selectedUsage && !isUsageLoading ? (
          <UsageOverview usage={selectedUsage} />
        ) : (
          <CreditUsageCardSkeleton />
        )}
      </div>
      <UsageBreakdown
        features={selectedUsage?.features ?? []}
        isLoading={isUsageLoading}
      />
    </section>
  );
};

interface UsageOverviewProps {
  usage: AdminApiAiUsage;
}

/** What the bar measures, against what it is measured out of. */
interface UsageBarReading {
  consumed: number;
  capacity: number;
  /** How the figures beneath the bar name what it counts. */
  caption: string;
}

/**
 * The bar's two figures. A period with live balances paces every credit it
 * can still spend, because that is the question while it is running. A closed
 * one measures the recurring grant alone: packs and promotional credits are
 * pools spanning many periods, so dividing by them would move the bar as a
 * pool drains rather than as usage happens. What the pools paid for, and what
 * was left of them, are named beside it instead.
 *
 * The grant draws down before any pack does, so a closed period's bar reaches
 * 100% exactly when the allowance ran out and never overshoots it.
 */
function resolveUsageBar(usage: AdminApiAiUsage): UsageBarReading | null {
  const capacity = resolvePeriodCreditCapacity(usage);
  if (capacity !== null) {
    return capacity > 0
      ? {
          consumed: usage.credits_used,
          capacity,
          caption: "credits used",
        }
      : null;
  }
  return usage.included_ai_credits !== null && usage.included_ai_credits > 0
    ? {
        consumed: usage.coverage.from_included_credits,
        capacity: usage.included_ai_credits,
        caption: "included credits used",
      }
    : null;
}

/** The card's heading and the figure opposite it. */
function describeAllowance(usage: AdminApiAiUsage): {
  title: string;
  headline: string;
} {
  return usage.balances.kind === "live"
    ? {
        title: "Monthly AI credits",
        headline: `${formatWholeNumber(usage.balances.remaining_credits)} left`,
      }
    : {
        title: "AI credits used",
        headline: formatWholeNumber(usage.credits_used),
      };
}

const UsageOverview: FC<UsageOverviewProps> = (props) => {
  const { usage } = props;

  const isCurrentPeriod = usage.selected_period.offset === 0;
  const usageBar = resolveUsageBar(usage);
  const { title, headline } = describeAllowance(usage);
  const projectedCredits = isCurrentPeriod ? projectPeriodTotal(usage) : null;

  // Usage against the period's total and the reset date only; the grant/pack
  // breakdown is left off deliberately to keep the card scannable.
  const details = [
    usageBar === null
      ? `${formatWholeNumber(usage.credits_used)} credits used`
      : formatUsageAgainstIncluded(
          usageBar.consumed,
          usageBar.capacity,
          usageBar.caption,
        ),
    usage.credits_overage > 0
      ? `${formatWholeNumber(usage.credits_overage)} uncovered`
      : null,
    isCurrentPeriod
      ? `Resets ${formatPeriodResetDate(usage.selected_period)}`
      : formatPeriodLabel(usage.selected_period),
  ].filter((detail) => detail !== null);

  return (
    <section className="rounded-md border border-border-light bg-white p-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="shrink-0 text-sm font-semibold text-foreground tabular-nums">
          {headline}
        </p>
      </div>

      {usageBar ? (
        <UsageBar
          className="mt-3 w-full"
          percentage={(usageBar.consumed / usageBar.capacity) * 100}
          isUncovered={usage.credits_overage > 0}
        />
      ) : null}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <p>
          {details.map((detail, index) => (
            <Fragment key={detail}>
              {index > 0 ? <span aria-hidden="true"> · </span> : null}
              {detail}
            </Fragment>
          ))}
        </p>
        <p className="text-right">
          {isCurrentPeriod
            ? projectedCredits !== null
              ? `Projected: ${formatWholeNumber(projectedCredits)} used by reset`
              : null
            : "Period ended"}
        </p>
      </div>
    </section>
  );
};

interface UsageBreakdownProps {
  features: readonly AdminApiAiUsageByFeature[];
  isLoading: boolean;
}

const UsageBreakdown: FC<UsageBreakdownProps> = (props) => {
  const { features: rows, isLoading } = props;

  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-foreground">
        Usage by feature
      </h2>
      <SettingsTable>
        <SettingsTableHeaderSection>
          <tr>
            <SettingsTableHeaderCell className="w-[45%]">
              Feature
            </SettingsTableHeaderCell>
            <SettingsTableHeaderCell className="w-[30%]">
              Volume
            </SettingsTableHeaderCell>
            <SettingsTableHeaderCell className="w-[25%] text-right">
              Credits used
            </SettingsTableHeaderCell>
          </tr>
        </SettingsTableHeaderSection>
        <SettingsTableBodySection>
          {isLoading ? <TableSkeleton /> : null}
          {rows.map((row) => (
            <tr className="hover:bg-gray-50" key={row.feature_key}>
              <SettingsTableDataCell>
                {getAiFeatureDefinition(row.feature_key)?.label ??
                  row.feature_key}
              </SettingsTableDataCell>
              <SettingsTableDataCell>
                {formatFeatureUnitCount(row)}
              </SettingsTableDataCell>
              <SettingsTableDataCell className="text-right tabular-nums">
                {formatWholeNumber(row.credits_used)}
              </SettingsTableDataCell>
            </tr>
          ))}
        </SettingsTableBodySection>
      </SettingsTable>
    </section>
  );
};

function formatFeatureUnitCount(feature: AdminApiAiUsageByFeature): string {
  const featureUnitNoun = USAGE_BASED_FEATURE_UNIT_NOUNS[feature.feature_key];
  const noun =
    feature.feature_unit_count === 1
      ? featureUnitNoun.singular
      : featureUnitNoun.plural;
  return `${formatWholeNumber(feature.feature_unit_count)} ${noun}`;
}
